import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';

/** Call after join/assign so nav and guards pick up the new role without a full reload. */
export function refreshMeRole() {
  window.dispatchEvent(new CustomEvent('me-role-changed'));
}

/**
 * Returns user role from DB only (GET /api/vendors/me-role). Never from Clerk.
 * On 404 or error, treats user as customer (isVendor: false).
 * @returns {{ isVendor: boolean, isOwner: boolean, loading: boolean }}
 */
export function useMeRole() {
  const { getToken, isSignedIn } = useAuth();
  const [role, setRole] = useState({ isVendor: false, isOwner: false, loading: true });
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const onRefresh = () => setRefreshTick((t) => t + 1);
    window.addEventListener('me-role-changed', onRefresh);
    return () => window.removeEventListener('me-role-changed', onRefresh);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isSignedIn) {
        if (!cancelled) setRole({ isVendor: false, isOwner: false, loading: false });
        return;
      }
      setRole((r) => ({ ...r, loading: true }));
      try {
        const res = await api('/api/vendors/me-role', { getToken });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) {
          setRole({ isVendor: !!data.isVendor, isOwner: !!data.isOwner, loading: false });
        } else {
          if (!cancelled) setRole({ isVendor: false, isOwner: false, loading: false });
        }
      } catch {
        if (!cancelled) setRole({ isVendor: false, isOwner: false, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, isSignedIn, refreshTick]);

  return role;
}
